import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import destinationsData from '../data/destinationsData';

function getActivityImage(activity, destination) {
  // Try to find a matching image from the destination's images or use a default
  if (destination && destination.image) return destination.image;
  return '/images/flowers.jpg';
}

function getActivityPrice(activity) {
  // Placeholder: assign a price based on keywords or default
  if (/boat|yacht|tour/i.test(activity)) return 110;
  if (/bonfire|beach/i.test(activity)) return 55;
  if (/mezcal|tasting/i.test(activity)) return 45;
  if (/market|art|walk/i.test(activity)) return 35;
  if (/spa|luxury/i.test(activity)) return 120;
  if (/golf/i.test(activity)) return 90;
  if (/snorkel/i.test(activity)) return 60;
  if (/cooking|class/i.test(activity)) return 50;
  if (/music|dance/i.test(activity)) return 40;
  return 60;
}

export default function GuestExperienceCalculator() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const destinationParam = params.get('destination');

  const [selectedDestination, setSelectedDestination] = useState(destinationParam || null);
  const [selectedExperiences, setSelectedExperiences] = useState([]);

  useEffect(() => {
    if (destinationParam) {
      setSelectedDestination(destinationParam);
    }
  }, [destinationParam]);

  const destinationList = destinationsData;
  const destination = selectedDestination
    ? destinationList.find(d => d.slug === selectedDestination)
    : null;
  const heroImg = destination?.heroImage || destination?.image || '/images/tulum-1200.jpg';
  const activities = destination?.topActivities || [];

  return (
    <div className="bg-white min-h-screen flex flex-col font-inter">
      {/* Hero Section */}
      <div className="relative w-full h-64 md:h-96 flex items-center justify-center mb-12">
        <img src={heroImg} alt="Destination Hero" className="w-full h-full object-cover object-center" style={{ opacity: 0.7 }} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
          <h1 className="text-3xl md:text-5xl font-playfair font-bold mb-2 text-white drop-shadow-lg">Design Your Dream Guest Experience</h1>
          <h2 className="text-lg md:text-2xl text-[#ffe9b3] mb-2 font-medium drop-shadow">Curate unforgettable moments for your wedding guests</h2>
        </div>
      </div>
      {/* Destination Carousel Selector */}
      <section className="w-full bg-white py-8 px-2 shadow-sm mb-12">
        <h3 className="text-xl font-semibold mb-4 text-[#181c24] text-center">1. Choose a Destination</h3>
        <div className="flex gap-4 overflow-x-auto pb-2 px-2 justify-center">
          {destinationList.map(dest => (
            <button
              key={dest.slug}
              className={`rounded-2xl shadow-md overflow-hidden border-2 transition-all duration-200 flex flex-col items-center min-w-[160px] max-w-[160px] focus:outline-none bg-white ${selectedDestination === dest.slug ? 'border-[#bfa76a] scale-105 ring-2 ring-[#bfa76a]' : 'border-transparent hover:border-[#bfa76a]/60'}`}
              onClick={() => setSelectedDestination(dest.slug)}
              style={{ height: 180 }}
            >
              <div className="w-full" style={{ height: 100 }}>
                <img src={dest.image} alt={dest.title} className="w-full h-full object-cover object-center" style={{ minHeight: 100, maxHeight: 100 }} />
              </div>
              <span className="py-2 text-sm font-medium text-[#181c24] bg-white w-full text-center flex-1 flex items-center justify-center" style={{ minHeight: 40 }}>{dest.title}</span>
            </button>
          ))}
        </div>
      </section>
      {/* Step 2: Experience Selector (only after destination is picked) */}
      {destination && (
        <>
          <section className="max-w-4xl mx-auto w-full px-4 mb-12 py-8 bg-white">
            <h3 className="text-xl font-semibold mb-4 text-[#181c24]">2. Pick Your Experiences</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {activities.map((activity, idx) => {
                const selected = selectedExperiences.includes(activity);
                return (
                  <div
                    key={activity}
                    className={`bg-white rounded-2xl shadow-md p-4 flex flex-col items-center border-2 transition-all duration-200 cursor-pointer ${selected ? 'border-[#bfa76a] scale-105 ring-2 ring-[#bfa76a] bg-[#fffbe9]' : 'border-transparent hover:border-[#bfa76a]/60'}`}
                    onClick={() => setSelectedExperiences(selected ? selectedExperiences.filter(k => k !== activity) : [...selectedExperiences, activity])}
                  >
                    <img src={getActivityImage(activity, destination)} alt={activity} className="w-full h-24 object-cover object-center rounded-xl mb-2" />
                    <div className="font-semibold text-[#181c24] mb-1 text-center">{activity}</div>
                    <div className="text-base font-bold text-[#bfa76a] mb-2">${getActivityPrice(activity)} <span className="text-xs font-normal text-[#888]">/person</span></div>
                    <button className={`mt-auto px-3 py-1 rounded-full text-xs font-bold transition-all ${selected ? 'bg-[#bfa76a] text-white' : 'bg-[#fafbfc] text-[#bfa76a] hover:bg-[#f3e6c6]'}`}>{selected ? 'Added' : 'Add +'}</button>
                  </div>
                );
              })}
            </div>
          </section>
          <hr className="max-w-4xl mx-auto border-t border-[#ececec] mb-12" />
        </>
      )}
      {/* Step 3 & 4: Only show if at least one experience is selected */}
      {selectedExperiences.length > 0 && (
        <>
          {/* Step 4: Bundle Summary */}
          <section className="max-w-2xl mx-auto w-full px-4 mb-12">
            <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col items-center border-2 border-[#bfa76a]/30 relative">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-4xl">🎉</div>
              <h3 className="text-2xl font-playfair font-bold mb-2 text-[#181c24]">Bundle Ready!</h3>
              <div className="mb-2 text-[#888] text-sm">You picked {selectedExperiences.length} magical experience{selectedExperiences.length !== 1 ? 's' : ''} for your guests!</div>
              <ul className="mb-4 text-left w-full max-w-xs mx-auto">
                {selectedExperiences.map(activity => (
                  <li key={activity} className="flex items-center gap-2 mb-1 text-[#181c24]">
                    <span className="text-[#bfa76a]">✓</span> {activity}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col items-center mb-4">
                <div className="text-lg font-semibold text-[#181c24]">Per Person: <span className="text-[#bfa76a] font-bold">${selectedExperiences.map(a => getActivityPrice(a)).reduce((sum, p) => sum + p, 0)}</span> USD</div>
              </div>
              <div className="flex flex-wrap gap-4 justify-center mt-2">
                <button className="bg-[#bfa76a] text-white px-6 py-2 rounded-full font-bold shadow hover:bg-[#a68d5d] transition flex items-center gap-2">Print Bundle</button>
                <button className="bg-[#5ec2b7] text-white px-6 py-2 rounded-full font-bold shadow hover:bg-[#4bb0a6] transition flex items-center gap-2">Share Link</button>
                <button className="bg-[#ececec] text-[#181c24] px-6 py-2 rounded-full font-bold shadow hover:bg-[#e0e0e0] transition flex items-center gap-2">Download PDF</button>
              </div>
              <div className="mt-4 text-[#888] text-sm text-center">Send this to your planner or group chat!</div>
            </div>
          </section>
        </>
      )}
      {/* Footer CTA */}
      <div className="bg-[#bfa76a] py-8 px-4 text-center mt-16">
        <h4 className="text-2xl font-playfair font-bold mb-2 text-white">Ready to bring these moments to life?</h4>
        <a href="/contact" className="inline-block bg-white text-[#bfa76a] font-bold py-3 px-8 rounded-full text-lg shadow hover:bg-[#f3e6c6] transition-all">Contact Us</a>
      </div>
      <Footer />
    </div>
  );
} 